import React from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as Core from '../../core';
import { HollowButton, SolidButton } from '../buttons-visuals';
import { ContentContainer } from '../containers';
import { useLeague } from '../../hooks/store';

import './index.scss';

const STUDY_GUIDES_URL = 'https://baylor.app.box.com/s/ssjssxg71dbbwdtypizj1p8qg5tzq473';

interface GettingStartedStep {
    id: string;
    step: string;
    title: string;
    description: string;
    icon: IconProp;
    tone: 'study' | 'solo' | 'showcase';
    action: React.ReactNode;
}

const SPOTLIGHT_CLASS = 'ncbc-spotlight';
const SPOTLIGHT_MS = 1800;
const spotlightTimers = new Map<string, number>();

const focusSection = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    el.classList.remove(SPOTLIGHT_CLASS);
    void el.offsetWidth;
    el.classList.add(SPOTLIGHT_CLASS);

    const existing = spotlightTimers.get(id);
    if (existing) window.clearTimeout(existing);
    spotlightTimers.set(
        id,
        window.setTimeout(() => {
            el.classList.remove(SPOTLIGHT_CLASS);
            spotlightTimers.delete(id);
        }, SPOTLIGHT_MS)
    );

    const heading = el.querySelector<HTMLElement>('h2, h3');
    heading?.focus({ preventScroll: true });
};

const GettingStarted = (): JSX.Element => {
    const league = useLeague();

    if (league?.featureGroup !== Core.Models.FeatureGroup.NCBC) return <></>;

    const steps: GettingStartedStep[] = [
        {
            id: 'study',
            step: '01',
            title: 'Study guides',
            description: 'Review the challenge materials so you know what to expect before you play.',
            icon: ['fas', 'book-open'],
            tone: 'study',
            action: (
                <SolidButton as="a" href={STUDY_GUIDES_URL} target="_blank" rel="noopener noreferrer" size="small">
                    Open study guides
                </SolidButton>
            ),
        },
        {
            id: 'solo',
            step: '02',
            title: 'Solo Play',
            description:
                'Practice on your own account whenever you want — no class session required. Scores land on your profile.',
            icon: ['fas', 'gamepad'],
            tone: 'solo',
            action: (
                <HollowButton as="button" size="small" onClick={() => focusSection('solo-play')}>
                    Show Solo Play
                </HollowButton>
            ),
        },
        {
            id: 'showcase',
            step: '03',
            title: 'Class showcases',
            description:
                'Live sessions your professor creates and runs at a scheduled time. Join with your class when one is posted.',
            icon: ['fas', 'users'],
            tone: 'showcase',
            action: (
                <HollowButton as="button" size="small" onClick={() => focusSection('showcases')}>
                    Show showcases
                </HollowButton>
            ),
        },
    ];

    return (
        <div className="getting-started-wrap">
            <ContentContainer
                className="getting-started shadow-medium p0"
                shade={Core.Models.Shades.Dark20}
                radius={Core.Models.RadiusSizes.Large}
            >
                <div className="getting-started__hero">
                    <div className="getting-started__hero-copy">
                        <p className="getting-started__kicker">Getting started</p>
                        <h2 className="getting-started__title">Get ready for the challenges</h2>
                        <p className="getting-started__lede">
                            Study the guides, practice Solo Play anytime on your own, then join class showcases when
                            your professor schedules them.
                        </p>
                    </div>
                    <div className="getting-started__hero-art" aria-hidden>
                        <span className="getting-started__hero-orb getting-started__hero-orb--a" />
                        <span className="getting-started__hero-orb getting-started__hero-orb--b" />
                        <span className="getting-started__hero-orb getting-started__hero-orb--c" />
                    </div>
                </div>

                <ol className="getting-started__grid">
                    {steps.map((step) => (
                        <li className={`getting-started__card getting-started__card--${step.tone}`} key={step.id}>
                            <div className="getting-started__card-visual" aria-hidden>
                                <span className="getting-started__card-step">{step.step}</span>
                                <FontAwesomeIcon className="getting-started__card-icon" icon={step.icon} />
                            </div>
                            <div className="getting-started__card-body">
                                <h3 className="getting-started__card-title">{step.title}</h3>
                                <p className="getting-started__card-copy">{step.description}</p>
                                <div className="getting-started__card-action">{step.action}</div>
                            </div>
                        </li>
                    ))}
                </ol>
            </ContentContainer>
        </div>
    );
};

export default GettingStarted;
